import { useRef } from "react";

import { applyCodeInput, codeFromField } from "@/lib/code-input";
import { cn } from "@/lib/utils";

import { InlineError } from "./inline-error";

const boxes = [0, 1, 2, 3, 4, 5] as const;
const DIGIT = /\d/;

const boxBorder = (invalid: boolean, filled: boolean): string => {
  if (invalid) {
    return "border-error";
  }
  if (filled) {
    return "border-navy-900";
  }
  return "border-n-600";
};

export const CodeInput = ({
  error,
  id,
  label,
  onChange,
  value,
}: {
  error: string | null;
  id: string;
  label: string;
  onChange: (value: string) => void;
  value: string;
}) => {
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const errorId = error ? `${id}-error` : undefined;

  const focusAt = (index: number) => {
    const input = inputs.current[index];
    input?.focus();
    input?.select();
  };

  const commit = (nextValue: string, focus: number) => {
    onChange(nextValue);
    window.setTimeout(() => focusAt(focus), 0);
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="m-0 font-medium text-sm" id={id}>
        {label}
      </p>
      <fieldset
        aria-describedby={errorId}
        aria-labelledby={id}
        className="m-0 grid grid-cols-6 gap-2.5 border-0 p-0"
      >
        {boxes.map((index) => {
          const digit = DIGIT.test(value[index] ?? "") ? value[index] : "";
          return (
            <input
              aria-invalid={error ? true : undefined}
              aria-label={`Digit ${index + 1} of 6`}
              autoComplete={index === 0 ? "one-time-code" : "off"}
              className={cn(
                "auth-focus box-border h-16 min-w-0 rounded-xl border bg-white text-center font-semibold text-[28px] text-navy-900 tabular-nums caret-coral-500",
                boxBorder(Boolean(error), Boolean(digit))
              )}
              inputMode="numeric"
              key={index}
              onChange={(event) => {
                const next = codeFromField(value, index, event.target.value);
                if (next.value !== value) {
                  commit(next.value, next.focus);
                }
              }}
              onFocus={(event) => event.target.select()}
              onKeyDown={(event) => {
                if (event.key === "Backspace") {
                  event.preventDefault();
                  const next = applyCodeInput(
                    { focus: index, value },
                    { index, type: "backspace" }
                  );
                  commit(next.value, next.focus);
                  return;
                }
                if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
                  event.preventDefault();
                  const next = applyCodeInput(
                    { focus: index, value },
                    {
                      direction: event.key === "ArrowLeft" ? -1 : 1,
                      index,
                      type: "arrow",
                    }
                  );
                  focusAt(next.focus);
                }
              }}
              onPaste={(event) => {
                event.preventDefault();
                const next = applyCodeInput(
                  { focus: index, value },
                  { text: event.clipboardData.getData("text"), type: "paste" }
                );
                commit(next.value, next.focus);
              }}
              ref={(node) => {
                inputs.current[index] = node;
              }}
              value={digit}
            />
          );
        })}
      </fieldset>
      <InlineError id={errorId} message={error} />
    </div>
  );
};
